import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Typography, FormHelperText } from '@mui/material';
import { TextEditor } from '../../../../../shared/components/textEditor/textEditor.component.tsx';
import { IIndividualDescriptionPopup } from './individualDescription.popup.tsx';
import { IPopupWrapper } from '../../../../../shared/components/popup/components/popup.component.tsx';
import { useBeforeClosePopup } from '../../../../../shared/components/popup/hooks/useBeforeClosePopup.tsx';

export const Content: FC<IContent> = ({ stream, initRow }) => {
  const { t } = useTranslation();
  const [row, setRow] = useState<IIndividualDescriptionPopup['row']>(structuredClone(initRow));
  const [isProductionTextValid, setIsProductionTextValid] = useState(true);
  const [isDeliveryTextValid, setIsDeliveryTextValid] = useState(true);
  useBeforeClosePopup({
    stream,
    isDirty: !(
      (initRow?.indTextDeliveryNote === row?.indTextDeliveryNote || !row?.indTextDeliveryNote) &&
      (initRow?.indTextProduction === row?.indTextProduction || !row?.indTextProduction)
    ),
  });
  useEffect(() => {
    stream.actionListener('open').subscribe(({ row }) => setRow(structuredClone(row)));
  }, []);
  const getText = (text: string | null) => {
    return (
      text &&
      text
        .replace(/(<(\/?[^>]+)>)/g, '')
        .replace(/&nbsp;/g, '')
        .trim()
    );
  };
  const clearValue = (value: string | null, prevValue: string) => {
    const onlyValueText = value && getText(value);
    if (onlyValueText) {
      if (prevValue !== value)
        return value
          .replace(/&nbsp;/g, '')
          .replace(/ +/g, ' ')
          .replace(/<p>/g, '')
          .replace(/<\/p>/g, '');
      else return prevValue;
    } else return onlyValueText;
  };
  return (
    <>
      <Typography
        variant='h5'
        sx={{
          mb: 2,
        }}
      >
        {t('order.text_on_production_list')}
      </Typography>
      <TextEditor
        onChange={(e) => {
          const indTextProduction = clearValue(e, row?.indTextProduction || '');
          if (indTextProduction && indTextProduction.length > 255) {
            setIsProductionTextValid(false);
            return;
          } else {
            setIsProductionTextValid(true);
          }
          setRow((prevVal: IIndividualDescriptionPopup['row']) => ({
            ...prevVal,
            indTextProduction,
          }));
        }}
        minHeight={86}
        toolbarItems={['bold']}
        defaultValue={row?.indTextProduction || row?.description}
      />
      {!isProductionTextValid && (
        <FormHelperText sx={{ color: 'error.main' }}>{t('offer.too_long_text')}</FormHelperText>
      )}
      <Typography
        variant='h5'
        sx={{
          mt: 4,
          mb: 2,
        }}
      >
        {t('order.text_on_delivery_note_invoice')}
      </Typography>
      <TextEditor
        onChange={(e) => {
          const indTextDeliveryNote = clearValue(e, row?.indTextDeliveryNote);
          if (indTextDeliveryNote && indTextDeliveryNote.length > 255) {
            setIsDeliveryTextValid(false);
            return;
          } else {
            setIsDeliveryTextValid(true);
          }
          setRow((prevVal: IIndividualDescriptionPopup['row']) => ({
            ...prevVal,
            indTextDeliveryNote,
          }));
        }}
        minHeight={86}
        toolbarItems={['bold']}
        defaultValue={row?.indTextDeliveryNote || row?.description}
      />
      {!isDeliveryTextValid && (
        <FormHelperText sx={{ color: 'error.main' }}>{t('offer.too_long_text')}</FormHelperText>
      )}
      <Stack
        direction='row'
        spacing={2}
        sx={{
          mt: 5,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={() => {
            stream.emit('save', { row });
            stream.emit('close');
          }}
          variant='contained'
          disabled={!isProductionTextValid || !isDeliveryTextValid}
        >
          {t('common.save')}
        </Button>
      </Stack>
    </>
  );
};

export interface IContent {
  stream: IPopupWrapper['stream'];
  initRow: IIndividualDescriptionPopup['row'];
}
